import {
  type ComputedRef,
  computed,
  type MaybeRefOrGetter,
  type Ref,
  ref,
  toValue,
} from 'vue';
import { sentryService } from '@/services/telemetry/sentry';
import { contentImageUrlWithSize } from '@/shared/utils/content-image-url';
import type { CustomerPortraitDownloadItem } from './customerPortraitDownloadTypes';

interface UseCustomerPortraitGalleryStateOptions {
  isUnmounted: MaybeRefOrGetter<boolean>;
  loadStartTime: MaybeRefOrGetter<number | null>;
  now?: () => number;
}

export interface CustomerPortraitGalleryState {
  firstImageLoadedTime: Ref<number | null>;
  galleryItems: ComputedRef<CustomerPortraitDownloadItem[]>;
  getErrorRate: () => number;
  imageErrorCount: Ref<number>;
  imageLoadCount: Ref<number>;
  initializePortraits: (items: readonly CustomerPortraitDownloadItem[]) => void;
  isInternalApiUrl: (url: string | undefined) => boolean;
  isUpscaledUrl: (url: string | undefined) => boolean;
  onPortraitLoad: (
    portrait: CustomerPortraitDownloadItem,
    index: number,
    event: Event
  ) => void;
  onPortraitLoadError: (portrait: CustomerPortraitDownloadItem) => void;
  portraitCurrentUrlByUuid: Ref<Record<string, string>>;
  portraits: Ref<CustomerPortraitDownloadItem[]>;
  retryLoadingPortrait: (index: number) => void;
  retryLoadingPortraitByUuid: (uuid: string) => void;
  setGalleryContext: () => void;
  updatePortrait: (
    uuid: string,
    diff: Partial<CustomerPortraitDownloadItem>
  ) => void;
}

export function useCustomerPortraitGalleryState(
  options: UseCustomerPortraitGalleryStateOptions
): CustomerPortraitGalleryState {
  const now = options.now ?? Date.now;
  const portraits = ref<CustomerPortraitDownloadItem[]>([]);
  const portraitCurrentUrlByUuid = ref<Record<string, string>>({});
  const firstImageLoadedTime = ref<number | null>(null);
  const imageLoadCount = ref(0);
  const imageErrorCount = ref(0);

  function isInternalApiUrl(url: string | undefined): boolean {
    return Boolean(url?.startsWith('/api/'));
  }

  function isUpscaledUrl(url: string | undefined): boolean {
    return Boolean(url && /[?&]variant=upscaled(?:&|$)/.test(url));
  }

  const galleryItems = computed<CustomerPortraitDownloadItem[]>(() =>
    portraits.value.map((portrait) => ({
      ...portrait,
      url: contentImageUrlWithSize(
        portraitCurrentUrlByUuid.value[portrait.uuid] || portrait.url,
        1200,
        90
      ),
    }))
  );

  function initializePortraits(
    items: readonly CustomerPortraitDownloadItem[]
  ): void {
    portraits.value = items.map((portrait) => ({
      ...portrait,
      downloadError: false,
      isPreloaded: false,
      isError: false,
      isDownloading: false,
      loadAttempt: 0,
      shareStatus: null,
    }));
    // Display URLs carry no variant param: the backend's default variant=auto
    // prefers upscaled and falls back to generated in ONE request. Hard-coded
    // upscaled here 404'd every portrait whose upscale never ran (THE-552).
    portraitCurrentUrlByUuid.value = Object.fromEntries(
      portraits.value.map((portrait) => [portrait.uuid, portrait.url])
    );
  }

  function updatePortrait(
    uuid: string,
    diff: Partial<CustomerPortraitDownloadItem>
  ): void {
    const portrait = portraits.value.find((item) => item.uuid === uuid);
    if (portrait) {
      Object.assign(portrait, diff);
    }
  }

  function onPortraitLoad(
    portrait: CustomerPortraitDownloadItem,
    _index: number,
    event: Event
  ): void {
    if (toValue(options.isUnmounted)) {
      return;
    }
    imageLoadCount.value++;

    const loadStartTime = toValue(options.loadStartTime);
    if (firstImageLoadedTime.value === null && loadStartTime !== null) {
      firstImageLoadedTime.value = now();
      sentryService.trackMetric(
        'download.time_to_first_image_ms',
        firstImageLoadedTime.value - loadStartTime,
        'millisecond'
      );
    }

    const image = event.currentTarget;
    const aspectRatio =
      image instanceof HTMLImageElement &&
      image.naturalWidth > 0 &&
      image.naturalHeight > 0
        ? image.naturalWidth / image.naturalHeight
        : portrait.aspectRatio;

    updatePortrait(portrait.uuid, {
      aspectRatio,
      isPreloaded: true,
      isError: false,
    });
  }

  // Internal display URLs carry no variant, so the server has already
  // resolved upscaled-or-generated: there is no second URL left to try and a
  // load error is terminal. External (greenscreen) URLs never had a fallback.
  function onPortraitLoadError(portrait: CustomerPortraitDownloadItem): void {
    if (toValue(options.isUnmounted)) {
      return;
    }
    const external = !isInternalApiUrl(
      portraitCurrentUrlByUuid.value[portrait.uuid]
    );

    imageErrorCount.value++;
    sentryService.addBreadcrumb(
      'image',
      external
        ? 'External image URL failed (no fallback available)'
        : 'Display image URL failed',
      external
        ? { uuid: portrait.uuid, url_type: 'external' }
        : { uuid: portrait.uuid },
      'error'
    );
    sentryService.incrementCounter('download.image_load_errors', 1, {
      error_type: external ? 'external_url_failed' : 'all_urls_failed',
    });
    updatePortrait(portrait.uuid, { isError: true });
  }

  function retryLoadingPortrait(index: number): void {
    const portrait = portraits.value[index];
    if (!portrait) {
      sentryService.addBreadcrumb(
        'image',
        'Retry failed: portrait not found at index',
        { index },
        'warning'
      );
      return;
    }
    retryLoadingPortraitByUuid(portrait.uuid);
  }

  function retryLoadingPortraitByUuid(uuid: string): void {
    const portrait = portraits.value.find((item) => item.uuid === uuid);
    if (!portrait) {
      sentryService.addBreadcrumb(
        'image',
        'Retry failed: portrait not found by UUID',
        { uuid },
        'warning'
      );
      return;
    }

    sentryService.addBreadcrumb(
      'image',
      'Portrait retry initiated',
      { uuid },
      'info'
    );
    updatePortrait(uuid, { isError: false, isPreloaded: false });
    updatePortrait(uuid, {
      loadAttempt: (portrait.loadAttempt ?? 0) + 1,
    });
    portraitCurrentUrlByUuid.value = {
      ...portraitCurrentUrlByUuid.value,
      // Reset to the regular URL — variant=auto resolves upscaled/generated
      // server-side (external URLs pass through unchanged).
      [uuid]: portrait.url,
    };
  }

  function setGalleryContext(): void {
    try {
      const hasExternalUrls = portraits.value.some(
        (portrait) => portrait.url && !isInternalApiUrl(portrait.url)
      );
      sentryService.setContext('download_gallery', {
        portrait_count: portraits.value.length,
        has_external_urls: hasExternalUrls,
        source_type: hasExternalUrls ? 'greenscreen' : 'kiosk-app',
      });
      sentryService.setTag(
        'source_type',
        hasExternalUrls ? 'greenscreen' : 'kiosk-app'
      );
    } catch {
      return;
    }
  }

  function getErrorRate(): number {
    const total = imageLoadCount.value + imageErrorCount.value;
    return total === 0 ? 0 : imageErrorCount.value / total;
  }

  return {
    firstImageLoadedTime,
    galleryItems,
    getErrorRate,
    imageErrorCount,
    imageLoadCount,
    initializePortraits,
    isInternalApiUrl,
    isUpscaledUrl,
    onPortraitLoad,
    onPortraitLoadError,
    portraitCurrentUrlByUuid,
    portraits,
    retryLoadingPortrait,
    retryLoadingPortraitByUuid,
    setGalleryContext,
    updatePortrait,
  };
}
